import { memo, useState, useRef } from "react";
import {
  Grid,
  useMediaQuery,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Divider,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BannerProps } from "Components/Banner";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import styled from "styled-components";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CarForm from "./CarType/Car";
import TruckForm from "./CarType/Truck";
import CoachForm from "./CarType/Coach";
import SpecializedVehicle from "./CarType/SpecializedVehicle";
import UploadRegion from "./CarType/uploadRegion";
import { ChildRef } from "./CarType/type";

const CoverBox = styled.div<BannerProps>`
  background: #ffffff;
  box-shadow: 0px 4px 25px rgba(102, 105, 133, 0.2);
  border-radius: 24px;
  position: relative;
  top: ${(props) => (props.isTabletOrMobile ? "5%" : "-5%")};
  overflow: auto;
`;

export type SelectOption = {
  id: string;
  label: string;
  key?: string;
  fuel?: string;
};

export type CarCompanyInfo = {
  carCompanyId: string;
  vehicleBrandName: string;
};

export type CarInfoModel = {
  carModelId: string;
  carModelName: string;
  year: number;
};

export type CarOption = {
  carOptionId: string;
  carOptionKey: string;
  optionName: string;
};

interface formProps {
  onSendType: (value: string) => void;
}

const FormAICycle = ({ onSendType }: formProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const carFormRef = useRef<ChildRef>(null);
  const [dataStringify, setDataStringify] = useState<string>("");
  const type = localStorage.getItem("type");
  const [carType, setCarType] = useState<string>(type || "car");
  const [files, setFiles] = useState([]);
  const [fileOthers, setFileOthers] = useState([]);
  const [paper, setPaper] = useState("two");
  const [dataFormImage, setDataFromImage] = useState<any>(null);

  const tablet = useMediaQuery(theme.breakpoints.up("md"));

  const handleValueChange = (value: string) => {
    setDataStringify(value);
  };

  const handleClear = () => {
    carFormRef.current?.triggerAction();
  };

  const handleChangeCarType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCarType((event.target as HTMLInputElement).value);
    onSendType((event.target as HTMLInputElement).value);
    setDataFromImage(null);
    setFiles([]);
    setFileOthers([]);
    handleClear();
  };

  const initialValues = {
    carCompanyId: "",
  };

  const handleSubmit = () => {
    const data = JSON.parse(dataStringify);
    localStorage.setItem("type", carType);
    localStorage.setItem(carType, dataStringify);
    navigate("/result", {
      state: {
        ...data,
        type: carType,
        ...(dataFormImage?.carValue &&
          dataFormImage?.minPrice &&
          dataFormImage?.maxPrice && {
            carValue: dataFormImage?.carValue,
            minPrice: dataFormImage?.minPrice,
            maxPrice: dataFormImage?.maxPrice,
          }),
      },
    });
  };

  return (
    <CoverBox
      isTabletOrMobile={true}
      style={{
        backgroundColor: theme.palette.mode === "dark" ? "#21262C" : "#ffffff",
      }}
    >
      <Formik onSubmit={handleSubmit} initialValues={initialValues}>
        {() => {
          return (
            <Form>
              <Grid
                container
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p="40px"
                rowGap="5px"
              >
                <Grid item xs={12}>
                  <div
                    style={{
                      color: theme.palette.mode === "dark" ? "#FFF" : "#111",
                      fontSize: 18,
                      fontStyle: "normal",
                      fontWeight: 600,
                    }}
                  >
                    {t("form.title.main")}
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <FormControl>
                    <RadioGroup
                      row
                      value={carType}
                      onChange={handleChangeCarType}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <FormControlLabel
                          value="car"
                          control={<Radio />}
                          label={
                            <Typography variant="body1">Xe con</Typography>
                          }
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#FFF"
                                : "#171B21",
                          }}
                        />
                        <FormControlLabel
                          value="truck"
                          control={<Radio />}
                          label={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography variant="body1">
                                Xe chở hàng hoá
                              </Typography>
                              <Tooltip
                                title={
                                  <Typography variant="h6">
                                    Xe tải, xe chở pallet, xe chở xe máy, xe
                                    ben, xe van, xe chở gia súc, xe chở gia cầm,
                                    xe tải khung mềm, xe chở ô tô, xe chở thức
                                    ăn gia súc,…
                                  </Typography>
                                }
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                              </Tooltip>
                            </div>
                          }
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#FFF"
                                : "#171B21",
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          ml: tablet ? 10 : 0,
                        }}
                      >
                        <FormControlLabel
                          value="specialized"
                          control={<Radio />}
                          label={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography variant="body1">
                                Xe chuyên dụng
                              </Typography>
                              <Tooltip
                                title={
                                  <Typography variant="h6">
                                    Xe bồn, xe lu, somi romooc, xe ép rác, xe
                                    nâng, xe đầu kéo, xe tải gắn cẩu, xe ủi, máy
                                    xúc, xe quét hút bụi rửa đường, xe hút, xe
                                    hooklit, xe cứu hỏa,…
                                  </Typography>
                                }
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                              </Tooltip>
                            </div>
                          }
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#FFF"
                                : "#171B21",
                          }}
                        />
                        <FormControlLabel
                          value="coach"
                          control={<Radio />}
                          label={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography variant="body1">Xe khách</Typography>
                              <Tooltip
                                title={
                                  <Typography variant="h6">
                                    Xe bus, xe khách, xe limousine, xe golf, xe
                                    điện chuyên dùng, xe điện chở khách, xe
                                    giường nằm, xe ghế ngồi,…
                                  </Typography>
                                }
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                              </Tooltip>
                            </div>
                          }
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#FFF"
                                : "#171B21",
                          }}
                        />
                      </Box>
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#4B5563" : "#D1D5DB",
                    }}
                  />
                </Grid>
                <UploadRegion
                  files={files}
                  setFiles={setFiles}
                  fileOthers={fileOthers}
                  setFileOthers={setFileOthers}
                  paper={paper}
                  setPaper={setPaper}
                  dataFormImage={dataFormImage}
                  setDataFromImage={setDataFromImage}
                  setCarType={setCarType}
                  handleClear={handleClear}
                />

                {carType === "car" && (
                  <CarForm
                    ref={carFormRef}
                    onValueChange={handleValueChange}
                    dataFormImage={dataFormImage}
                    setDataFromImage={setDataFromImage}
                  />
                )}
                {carType === "truck" && (
                  <TruckForm
                    ref={carFormRef}
                    onValueChange={handleValueChange}
                    dataFormImage={dataFormImage}
                    setDataFromImage={setDataFromImage}
                  />
                )}
                {carType === "coach" && (
                  <CoachForm
                    ref={carFormRef}
                    onValueChange={handleValueChange}
                    dataFormImage={dataFormImage}
                    setDataFromImage={setDataFromImage}
                  />
                )}
                {carType === "specialized" && (
                  <SpecializedVehicle
                    ref={carFormRef}
                    onValueChange={handleValueChange}
                    dataFormImage={dataFormImage}
                    setDataFromImage={setDataFromImage}
                  />
                )}
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CoverBox>
  );
};

export default memo(FormAICycle);
